import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { CalendarDays, MapPin, Users, DollarSign, PartyPopper, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import InsuranceWarningBanner from "@/components/InsuranceWarningBanner";

/**
 * PrivateBookingDialog — the "Book me for a private event" CTA on a
 * vendor's public profile. Triggered by scanning the vendor's QR (or any
 * visitor to `/vendors/{slug}`).
 *
 * Flow (per user-agreed spec):
 *   1. Customer fills in event details + proposes a budget + shares their
 *      wishlist (menu items, decor, vibe).
 *   2. Vendor gets emailed + in-app notified. They respond with a quote
 *      (or decline) from their /vendor/bookings inbox.
 *   3. Customer gets emailed a signed link back to /book/{id}?t=... to
 *      pay through Stripe Checkout.
 *
 * We only show this dialog when the vendor has Stripe Connect ready
 * (`stripe_connect_charges_enabled`). Otherwise `disabledReason` renders
 * an inline explanation instead of the CTA.
 *
 * Insurance banner is shown TWICE — once at the top of the dialog (before
 * they enter details) and again just above the submit button (before they
 * commit). This mirrors the email + payment-page repetition so the
 * "only pay through the app" message is impossible to miss.
 */
export default function PrivateBookingDialog({ vendorSlug, vendorName, canBook, disabledReason }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    event_date: "",
    event_time: "",
    event_type: "",
    event_location: "",
    guest_count: "",
    proposed_budget_cents: "",
    wishlist: "",
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (busy) return;
    const budget = Math.round(parseFloat(form.proposed_budget_cents || 0) * 100);
    if (!(budget >= 100)) { toast.error("Enter a budget of at least $1"); return; }
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      toast.error("Your name and email are required"); return;
    }
    if (!form.event_date || !form.event_type.trim() || !form.event_location.trim()) {
      toast.error("Date, event type, and location are required"); return;
    }
    setBusy(true);
    try {
      const payload = {
        vendor_slug: vendorSlug,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim().toLowerCase(),
        customer_phone: form.customer_phone.trim() || null,
        event_date: form.event_date,
        event_time: form.event_time || null,
        event_type: form.event_type.trim(),
        event_location: form.event_location.trim(),
        guest_count: form.guest_count ? parseInt(form.guest_count, 10) : null,
        proposed_budget_cents: budget,
        wishlist: form.wishlist.trim() || null,
      };
      await api.post("/private-bookings", payload);
      toast.success(`Request sent to ${vendorName}. They'll get back to you by email with a quote.`);
      setOpen(false);
      setForm({
        customer_name: "", customer_email: "", customer_phone: "",
        event_date: "", event_time: "", event_type: "", event_location: "",
        guest_count: "", proposed_budget_cents: "", wishlist: "",
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Could not send your request";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (!canBook) {
    return (
      <div
        className="rounded-md border p-3 text-xs"
        style={{
          borderColor: "var(--border-warm)",
          backgroundColor: "var(--sand)",
          color: "var(--ink-soft)",
        }}
        data-testid="booking-disabled-notice"
      >
        {disabledReason ||
          "This vendor hasn't set up payments yet — private bookings will open once they do."}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="btn-primary rounded-full w-full"
          data-testid="private-booking-open"
        >
          <PartyPopper className="h-4 w-4 mr-2" />
          Book {vendorName || "this vendor"} for a private event
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-lg"
        data-testid="private-booking-dialog"
        style={{ backgroundColor: "var(--paper-warm)" }}
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Book {vendorName} for your event
          </DialogTitle>
          <DialogDescription className="text-sm" style={{ color: "var(--ink-soft)" }}>
            Share your event details + a budget. {vendorName} will reply with a
            quote by email — you only pay if you accept.
          </DialogDescription>
        </DialogHeader>

        <InsuranceWarningBanner compact />

        <div className="grid gap-3 mt-2">
          <Row>
            <Field label="Your name" required>
              <Input value={form.customer_name}
                     onChange={(e) => update({ customer_name: e.target.value })}
                     data-testid="booking-customer-name" />
            </Field>
            <Field label="Your email" required>
              <Input type="email" value={form.customer_email}
                     onChange={(e) => update({ customer_email: e.target.value })}
                     data-testid="booking-customer-email" />
            </Field>
          </Row>
          <Field label="Phone (optional)">
            <Input value={form.customer_phone}
                   onChange={(e) => update({ customer_phone: e.target.value })}
                   data-testid="booking-customer-phone" />
          </Field>
          <Row>
            <Field label="Event date" required icon={CalendarDays}>
              <Input type="date" value={form.event_date}
                     onChange={(e) => update({ event_date: e.target.value })}
                     data-testid="booking-event-date" />
            </Field>
            <Field label="Start time" icon={CalendarDays}>
              <Input type="time" value={form.event_time}
                     onChange={(e) => update({ event_time: e.target.value })}
                     data-testid="booking-event-time" />
            </Field>
          </Row>
          <Field label="Event type" required
                 hint="e.g. birthday, corporate, wedding shower, backyard BBQ">
            <Input value={form.event_type}
                   onChange={(e) => update({ event_type: e.target.value })}
                   data-testid="booking-event-type" />
          </Field>
          <Field label="Location / address" required icon={MapPin}>
            <Input value={form.event_location}
                   onChange={(e) => update({ event_location: e.target.value })}
                   data-testid="booking-event-location" />
          </Field>
          <Row>
            <Field label="Guest count" icon={Users}>
              <Input type="number" min="1" value={form.guest_count}
                     onChange={(e) => update({ guest_count: e.target.value })}
                     data-testid="booking-guest-count" />
            </Field>
            <Field label="Your budget (USD)" required icon={DollarSign}
                   hint="They'll send back a firm quote">
              <Input type="number" min="1" step="1"
                     placeholder="500"
                     value={form.proposed_budget_cents}
                     onChange={(e) => update({ proposed_budget_cents: e.target.value })}
                     data-testid="booking-budget" />
            </Field>
          </Row>
          <Field label="What would you like at your event?"
                 hint="Share menu items, decor style, entertainment, must-haves — anything that helps them quote right.">
            <Textarea rows={4} className="resize-none" value={form.wishlist}
                      onChange={(e) => update({ wishlist: e.target.value })}
                      data-testid="booking-wishlist"
                      placeholder="Napa cabbage tacos, peach cobbler bar, kid-friendly mocktails…" />
          </Field>
        </div>

        <div className="mt-2">
          <InsuranceWarningBanner compact />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)}
                  data-testid="booking-cancel" className="rounded-full">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}
                  data-testid="booking-submit"
                  className="btn-primary rounded-full">
            {busy ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <><Send className="h-4 w-4 mr-2" /> Send request</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, required, children, hint, icon: Icon }) {
  return (
    <label className="block">
      <div className="flex items-center gap-1 text-[11px] uppercase tracking-widest mb-1"
           style={{ color: "var(--ink-soft)" }}>
        {Icon && <Icon className="h-3 w-3" />}
        {label}
        {required && <span style={{ color: "var(--avt-pink)" }}>*</span>}
      </div>
      {children}
      {hint && (
        <div className="text-[11px] mt-1" style={{ color: "var(--ink-soft)" }}>{hint}</div>
      )}
    </label>
  );
}
